/**
 * PKCE (RFC 7636) helpers for the KochID Authorization Code flow.
 *
 * The code verifier is a high-entropy random string; the challenge is its
 * SHA-256 digest, base64url-encoded. KochID advertises S256 in
 * `code_challenge_methods_supported`, which is the only method used here —
 * `plain` is never used, as it provides no protection against interception.
 */

/** Base64url-encode bytes without padding (RFC 7636 §A). */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Generate a cryptographically random string suitable for a verifier or state. */
function randomUrlSafeString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/**
 * Create a PKCE code verifier. 64 random bytes encodes to 86 characters,
 * comfortably inside RFC 7636's 43–128 character range.
 */
export function createCodeVerifier(): string {
  return randomUrlSafeString(64);
}

/** Create an opaque `state` value for CSRF protection on the callback. */
export function createState(): string {
  return randomUrlSafeString(32);
}

/** Derive the S256 code challenge for a verifier. */
export async function deriveCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
}
